import MDBox from "components/MDBox";
import { useEffect, useState, useRef, useMemo } from "react";
import { getJobSuccessionPlanData, updateJobCriticality } from "services/api";
import {
  Autocomplete,
  Typography,
  Grid,
  TextField,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import WorkIcon from "@mui/icons-material/Work";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { fieldStyles } from "services/utils";
import LoadingScreen from "components/Loading";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const SuccessionPlanSteps = () => {
  const { t } = useTranslation();
  const snackbarRef = useRef();
  const [jobs, setJobs] = useState([]); // The original full list from DB
  const [departments, setDepartments] = useState([]); // The dropdown options
  const [employees, setEmployees] = useState([]); // The dropdown options
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getJobSuccessionPlanData();

        if (data !== "error") {
          // 2. Store the jobs (table) and departments (dropdown) separately
          setJobs(data.jobsWithDepartments);
          setDepartments(data.allDepartments);
          setEmployees(data.allEmployees);
        } else {
          setJobs([]);
          setDepartments([]);
          setEmployees([]);
        }
      } catch (error) {
        console.error("Error fetching succession plan data:", error);

        // fallback state
        setJobs([]);
        setDepartments([]);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCardClick = (job) => {
    navigate("/successors", {
      state: {
        job, //selected job to see its succession plan
        employees, // all employees in company
        jobs, // all jobs with their departments
      },
    });
  };

  const handleToggleCriticality = async (jobId, isCritical) => {
    // Find the selected job
    const selectedJob = jobs.find((j) => j.jobId === jobId);

    // Prevent changing to non-critical if successor count is 0
    if (!isCritical && selectedJob?.successorCount > 0) {
      snackbarRef.current?.showSnackbar(
        t("admin.successionPlanSteps.cannotRemoveCriticality"),
        "error"
      );
      return;
    }
    // 1. Update UI locally first for a "snappy" feel
    setJobs((prevJobs) =>
      prevJobs.map((j) => (j.jobId === jobId ? { ...j, jobCriticality: isCritical } : j))
    );

    // 2. Sync with the database
    const result = await updateJobCriticality(jobId, isCritical);

    // 3. Optional: If the server fails, revert the change and show an alert
    if (result === "error") {
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.criticalityError"), "error");
      setJobs((prevJobs) =>
        prevJobs.map((j) => (j.jobId === jobId ? { ...j, jobCriticality: !isCritical } : j))
      );
    }
  };

  const renderJobCard = (job) => (
    <Grid item xs={12} sm={6} md={4} key={job.jobId}>
      <Paper
        elevation={3}
        // onClick={() => handleCardClick(job)}
        onClick={() => job.jobCriticality && handleCardClick(job)}
        sx={{
          p: 2,
          pb: 2,
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          borderLeft: job.jobCriticality ? "6px solid #d32f2f" : "6px solid #2e7d32",
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          {/* Left Side: Icon + Name */}
          {/* <MDBox display="flex" alignItems="center">
            <WorkIcon color="info" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              {job.jobName || t("admin.successionPlanSteps.unnamedJob")}{" "}
            </Typography>
          </MDBox> */}
          <MDBox
            display="flex"
            alignItems="center"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              flex: 1,
            }}
          >
            <WorkIcon color="info" sx={{ mr: 1, flexShrink: 0 }} />

            <Tooltip
              title={isLongJobName ? job.jobName || "" : ""}
              arrow
              disableHoverListener={!isLongJobName}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {job.jobName || t("admin.successionPlanSteps.unnamedJob")}
              </Typography>
            </Tooltip>
          </MDBox>

          {/* Right Side: Toggle Switch */}
          <FormControlLabel
            sx={{ mr: 0 }} // Remove default right margin
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            control={
              <Switch
                checked={job.jobCriticality}
                // onChange={(e) => handleToggleCriticality(job.jobId, e.target.checked)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleToggleCriticality(job.jobId, e.target.checked);
                }}
                color="primary"
              />
            }
            label={
              <Typography
                variant="button"
                fontWeight="bold"
                sx={{ fontSize: "10px", display: { xs: "none", lg: "block" } }} // Hide text on small screens if too tight
                color="textSecondary"
              ></Typography>
            }
            labelPlacement="start" // Puts the label text to the left of the switch
          />
        </MDBox>
        <Divider sx={{ my: 1 }} />

        <MDBox display="flex" alignItems="center">
          <Typography variant="body2" color="text" sx={{ pl: 4 }}>
            <strong>{t("admin.dashboard.department")}:</strong> {job.departmentName}
          </Typography>
        </MDBox>

        <MDBox display="flex" alignItems="center" mb={0.5}>
          <Typography variant="body2" color="text" sx={{ pl: 4 }}>
            <strong>{t("admin.successionPlanSteps.successorCount")}:</strong>{" "}
            {job.successorCount || 0}
          </Typography>
        </MDBox>

        {/* Line 3: Closest Successor */}
        <MDBox display="flex" alignItems="center">
          <Typography variant="body2" color="text" sx={{ pl: 4 }}>
            <strong>{t("admin.successionPlanSteps.closest")}:</strong>{" "}
            {!job.closestSuccessor || job.closestSuccessor === "None" ? "-" : job.closestSuccessor}
          </Typography>
        </MDBox>
      </Paper>
    </Grid>
  );

  const { jobsForDropdown, criticalJobs, nonCriticalJobs } = useMemo(() => {
    // 1. Logic for the Job Dropdown: Restricted by department selection
    const dropdown = selectedDeptId ? jobs.filter((j) => j.departmentId === selectedDeptId) : jobs;

    // 2. Logic for filtering all cards based on both selectors
    const masterFiltered = jobs.filter((j) => {
      const matchesDept = selectedDeptId ? j.departmentId === selectedDeptId : true;
      const matchesJob = selectedJobId ? j.jobId === selectedJobId : true;
      return matchesDept && matchesJob;
    });

    // 3. Split into sections and Sort alphabetically
    const critical = masterFiltered
      .filter((j) => j.jobCriticality === true)
      .sort((a, b) => (a.jobName || "").localeCompare(b.jobName || ""));

    const nonCritical = masterFiltered
      .filter((j) => !j.jobCriticality)
      .sort((a, b) => (a.jobName || "").localeCompare(b.jobName || ""));

    return {
      jobsForDropdown: dropdown,
      criticalJobs: critical,
      nonCriticalJobs: nonCritical,
    };
  }, [jobs, selectedDeptId, selectedJobId]); // Runs only when data or filters change

  if (loading) {
    return <LoadingScreen />;
  }

  const isLongJobName = (jobName) => (jobName || "").length > 25;

  return (
    <MDBox p={3}>
      {/* FILTER HEADER */}
      <MDBox mb={4} display="flex" flexWrap="wrap" gap={2} alignItems="center">
        {/* DEPARTMENT FILTER */}
        <Autocomplete
          size="small"
          options={departments}
          getOptionLabel={(option) => option?.departmentName || ""}
          value={departments.find((d) => d.departmentId === selectedDeptId) || null}
          onChange={(event, newValue) => {
            setSelectedDeptId(newValue ? newValue.departmentId : null);
            setSelectedJobId(null);
          }}
          sx={{
            width: {
              xs: "100%",
              sm: 250,
            },
            minWidth: 0,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.successionPlanSteps.filterDepartment")}
              variant="outlined"
              sx={fieldStyles}
            />
          )}
          isOptionEqualToValue={(option, value) => option.departmentId === value.departmentId}
        />

        {/* JOB FILTER */}
        <Autocomplete
          size="small"
          options={jobsForDropdown}
          getOptionLabel={(option) => option?.jobName || ""}
          value={jobsForDropdown.find((j) => j.jobId === selectedJobId) || null}
          onChange={(event, newValue) => {
            setSelectedJobId(newValue ? newValue.jobId : null);
          }}
          sx={{
            width: {
              xs: "100%",
              sm: 250,
            },
            minWidth: 0,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.successionPlanSteps.filterJob")}
              variant="outlined"
              sx={fieldStyles}
            />
          )}
          isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
        />
      </MDBox>

      {/* SECTION 1: CRITICAL JOBS */}
      <MDBox mb={2}>
        <MDBox display="flex" alignItems="center" mb={2}>
          <WarningAmberIcon color="error" sx={{ mr: 1 }} />
          <Typography variant="h5" color="error" fontWeight="bold">
            {t("admin.successionPlanSteps.criticalJobs")} ({criticalJobs.length}){" "}
          </Typography>
        </MDBox>
        <Grid container spacing={3}>
          {criticalJobs.length > 0 ? (
            criticalJobs.map((job) => renderJobCard(job))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" color="textSecondary" sx={{ fontStyle: "italic" }}>
                {t("admin.successionPlanSteps.noCriticalJobs")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </MDBox>

      <Divider sx={{ my: 2 }} />

      {/* SECTION 2: NON-CRITICAL JOBS */}
      <MDBox>
        <MDBox display="flex" alignItems="center" mb={2}>
          <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
          <Typography variant="h5" color="success" fontWeight="bold">
            {t("admin.successionPlanSteps.standardJobs")} ({nonCriticalJobs.length}){" "}
          </Typography>
        </MDBox>
        <Grid container spacing={3}>
          {nonCriticalJobs.length > 0 ? (
            nonCriticalJobs.map((job) => renderJobCard(job))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" color="textSecondary" sx={{ fontStyle: "italic" }}>
                {t("admin.successionPlanSteps.noStandardJobs")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </MDBox>
  );
};

export default SuccessionPlanSteps;
